import noop from 'lodash/noop';
import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Controller from 'core/components/FormController';
import Dialog from 'pages/Dashboard/components/Dialog';
import isNil from 'lodash/isNil';
import {
  ToDoStatus,
} from 'types/toDo';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import AutoCompletedInput from 'core/components/AutoComplete';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import StatusSelect from 'pages/Dashboard/pages/Todos/components/StatusSelect';
import {
  useCreateToDo,
  useToDoSettings,
} from 'pages/Dashboard/pages/Todos/hooks';
import Button from 'pages/Dashboard/components/Button';
import PatientsAutocomplete from 'pages/Dashboard/pages/Todos/components/PatientSelect';
import Input from 'pages/Dashboard/components/Input';
import {
  useNotify,
} from 'core/hooks';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  CreateToDoRequestDTO,
  UserDTO,
} from 'dtos';
import {
  getDisplayName,
  normalizeUser,
} from 'pages/Dashboard/utils/helper';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Checkbox from 'pages/Dashboard/components/Input/Checkbox';
import {
  useFormAutoFocus,
} from 'views/EMR/hooks/common';
import TextArea from 'core/components/TextArea';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import {
  useGetUsers,
} from 'pages/Dashboard/hooks/providers';
import {
  formatDateOnly,
} from 'utils/date';
import {
  getUserIds,
  useToDoMentions,
} from 'pages/Dashboard/pages/Todos/mentions';
import CollaboratorsAutoComplete from 'pages/Dashboard/pages/Todos/components/CollaboratorsAutoComplete';
import useActiveStatusOptions from 'pages/Dashboard/hooks/useActiveStatusOptions';
import ProviderLookupListDropdown from 'views/EMR/components/ProviderLookup/Dropdown';
import DropdownOptionRow from 'core/components/Lookup/DropdownOptionRow';
import clsx from 'clsx';

export type ToDoDialogParams = {
  patient?: PatientCompact | null;
  faxId?: number | null;
  onClose: () => void;
};

type ToDoCreateData = {
  patient: PatientCompact | null;
  toDoName: string;
  isUrgent: boolean;
  category: string | null;
  assignee: UserDTO | null;
  collaborators: UserDTO[];
  dueDate: Dayjs | null;
  status: ToDoStatus;
  comment: string;
};

const defaultValues: Partial<ToDoCreateData> = {
  patient: null,
  toDoName: '',
  isUrgent: false,
  category: null,
  dueDate: null,
  status: { transitionName: 'NotStarted', statusDisplayName: 'Not Started' },
  comment: '',
};

function getSelectableToDoUsers(users: UserDTO[] = []) {
  return users.filter((user) => !user?.isServiceAccount);
}

export default function CreateToDo({
  patient,
  onClose,
  faxId,
}: ToDoDialogParams) {
  const userInfo = useUserInfo();
  const isPatientProvided = !isNil(patient);
  const inputRef = useFormAutoFocus();

  const currentUser = useMemo(
    () => normalizeUser(userInfo?.user ?? {}, userInfo?.user?.userId),
    [userInfo],
  );

  const formProps = useForm<ToDoCreateData>({
    mode: 'onChange',
    defaultValues: {
      assignee: currentUser ?? null,
      collaborators: [],
      ...defaultValues,
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    formState: { isValid, isSubmitting },
  } = formProps;

  const notify = useNotify();
  const { mutateAsync: createToDo, isPending: isCreating } = useCreateToDo();
  const {
    categories: validCategories,
    statuses: validStatuses,
  } = useToDoSettings();
  const { data: users = [], isLoading: usersLoading } = useGetUsers({ onlyDoctors: false });
  const assigneeOptions = useMemo(
    () => getSelectableToDoUsers(users),
    [users],
  );
  const {
    visibleOptions: visibleAssigneeOptions,
    inactiveConfig: assigneeInactiveConfig,
  } = useActiveStatusOptions(assigneeOptions, 'user');
  const {
    mentionsConfig,
    handleEditorChange,
    resetTaggedUserIds,
    getMergedTaggedUserIds,
  } = useToDoMentions();

  useEffect(() => {
    const currentPatient = getValues('patient');
    if (!isNil(patient) && patient?.patientId !== currentPatient?.patientId) {
      setValue('patient', patient);
    }
  }, [patient]);

  const handleClose = () => {
    resetTaggedUserIds();
    reset();
    onClose();
  };

  const faxToDo = !isNil(faxId) ? { faxId } : null;

  const onSubmit = async (data: ToDoCreateData) => {
    const {
      isUrgent,
      patient,
      assignee,
      collaborators,
      category,
      dueDate,
      toDoName,
      comment,
      status,
    } = data;

    await createToDo({
      patientId: patient?.patientId,
      assigneeUserId: assignee?.userId,
      taggedUserIds: getMergedTaggedUserIds(getUserIds(collaborators)),
      category: category ?? '',
      dueDate: dueDate ? formatDateOnly(dayjs(dueDate)) : null,
      statusDisplayName: status.transitionName,
      comment,
      toDoName,
      isUrgent,
      faxToDo,
    } as CreateToDoRequestDTO);
    notify({
      variant: 'success',
      message: 'New To-do was created successfully!',
    });
    handleClose();
  };

  return (
    <Dialog
      open
      title="Create To-Do"
      onClose={handleClose}
      onConfirm={noop}
      confirmText="Create"
      className="!w-140 !space-y-0 overflow-y-hidden !p-0"
      titleClassName="p-5 pb-2"
      autoFocusOptions={{ enabled: isNil(patient) }}
      maxWidth="lg"
    >
      <FormProvider {...formProps}>
        <div className="flex flex-col gap-form-row overflow-y-auto px-5">
          <Controller
            name="patient"
            control={control}
            rules={{ required: 'Select patient' }}
            render={({ field, fieldState: { invalid } }) => (
              <PatientsAutocomplete
                {...field}
                required
                allowDefault
                disablePortal
                id="to-do-patient"
                placeholder="Enter patient name, phone or date of birth"
                error={invalid}
                className="!mb-0"
                inputRef={isPatientProvided ? undefined : inputRef}
              />
            )}
          />
          <Controller
            name="category"
            control={control}
            rules={{ required: 'Select category' }}
            render={({ field, fieldState: { invalid } }) => (
              <Wrapper
                id="to-do-category"
                required
                label="Category"
                labelClasses="font-semibold text-xs text-gray-500"
                error={invalid}
                showErrorText={false}
              >
                <AutoCompletedInput<string>
                  {...field}
                  id="to-do-category"
                  openOnFocus
                  disablePortal
                  className="!w-full"
                  options={validCategories}
                  getLabel={(val) => val}
                  placeholder="Choose To-do category"
                  inputRef={isPatientProvided ? inputRef : undefined}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="toDoName"
            control={control}
            rules={{
              required: 'Enter title',
              maxLength: 64,
            }}
            render={({ field, fieldState: { invalid } }) => (
              <Input
                {...field}
                required
                error={invalid}
                showErrorText={false}
                labelClasses="font-semibold text-xs text-gray-500"
                label="Title"
                inputProps={{ maxLength: 64 }}
                allowSpeechToText
                placeholder="Enter To-do title here"
                id="to-do-name"
                trackingLabel="Create To Do Title"
              />
            )}
          />
          <Controller
            name="status"
            control={control}
            rules={{ required: 'Select status' }}
            render={({ field, fieldState: { invalid } }) => (
              <Wrapper
                id="to-do-status"
                required
                className="!w-full"
                containerClassName="!w-full"
                label="Status"
                labelClasses="font-semibold text-xs text-gray-500"
                error={invalid}
                showErrorText={false}
              >
                <StatusSelect
                  id="to-do-status"
                  {...field}
                  className="!w-full"
                  options={validStatuses}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="comment"
            control={control}
            render={
            ({ field }) => (
              <Wrapper
                id="to-do-comments-wrapper"
                label="Comments"
                labelClasses="font-semibold text-xs text-gray-500"
                showErrorText={false}
              >
                <TextArea
                  {...field}
                  {...mentionsConfig}
                  trackingLabel="Create To Do Comments"
                  id="to-do-comment"
                  placeholder="Your message is here..."
                  actions={['dictation']}
                  classes={{ textarea: 'max-w-[608px]' }}
                  onEditorChange={handleEditorChange}
                />
              </Wrapper>
            )
          }
          />
          <Controller
            name="dueDate"
            control={control}
            render={({ field, fieldState: { invalid } }) => (
              <Wrapper
                id="to-do-due-date"
                className="!w-full"
                label="Due Date"
                labelClasses="font-semibold text-xs text-gray-500"
                error={invalid}
                showErrorText={false}
              >
                <DatePicker
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  className="!w-full"
                  containerClassName="!w-full"
                  inputContainerClassName="!w-full"
                  placeholder="mm/dd/yyyy"
                  showDateButtons={false}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="assignee"
            control={control}
            rules={{ required: 'Select assignee' }}
            render={({ field, fieldState: { invalid } }) => (
              <Wrapper
                id="to-do-assignee"
                required
                label="Assign To"
                labelClasses="font-semibold text-xs text-gray-500"
                error={invalid}
                showErrorText={false}
              >
                <AutoCompletedInput<UserDTO>
                  {...field}
                  id="to-do-assignee"
                  blurOnSelect
                  openOnFocus
                  disablePortal
                  className="!w-full"
                  options={visibleAssigneeOptions}
                  disabled={usersLoading}
                  includeInputInList
                  placeholder="Select assignee"
                  getLabel={(user) => getDisplayName(user.formattedNames)}
                  isOptionEqualToValue={(a, b) => a.userId === (b as UserDTO).userId}
                  classes={{ inputRoot: '!pr-0' }}
                  dropdownClassName="!overflow-hidden p-2"
                  slotProps={{
                    listbox: {
                      component: ProviderLookupListDropdown,
                      inactiveConfig: assigneeInactiveConfig,
                    },
                  } as Object}
                  renderOption={(optionProps, option) => (
                    <DropdownOptionRow
                      key={option.userId}
                      id={option.userId?.toString()}
                      label={getDisplayName(option.formattedNames, '--')}
                      props={optionProps}
                    >
                      <span className={clsx({ 'text-gray-400': option.isActive === false })}>
                        {getDisplayName(option.formattedNames, '--')}
                      </span>
                    </DropdownOptionRow>
                  )}
                  value={field.value}
                  onChange={(value: UserDTO | UserDTO[]) => {
                    if (!isNil(value)) {
                      field.onChange(value as UserDTO);
                    }
                  }}
                />
              </Wrapper>
            )}
          />
          <Controller
            name="collaborators"
            control={control}
            render={({ field, fieldState: { invalid } }) => (
              <Wrapper
                id="to-do-collaborators"
                label="Collaborator"
                labelClasses="font-semibold text-xs text-gray-500"
                error={invalid}
                showErrorText={false}
              >
                <CollaboratorsAutoComplete
                  {...field}
                  onBlur={field.onBlur}
                  id="to-do-collaborators"
                  options={assigneeOptions}
                  className="!w-full"
                  disabled={usersLoading}
                  loading={usersLoading}
                  disablePortal
                />
              </Wrapper>
            )}
          />
          <Controller
            name="isUrgent"
            control={control}
            render={({ field: { value, ...field } }) => (
              <Checkbox
                {...field}
                id="is-urgent-toggle"
                checked={Boolean(value)}
                value={(value ?? '').toString()}
                label="Urgent"
              />
            )}
          />
        </div>
        <div className="flex flex-row-reverse justify-between items-center p-5 nw-create-to-do-buttons">
          <DisabledFormTooltip>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={handleSubmit(onSubmit)}
              disabled={!isValid || isSubmitting || isCreating}
            >
              {isCreating ? 'Creating...' : 'Create'}
            </Button>
          </DisabledFormTooltip>
          <Button
            variant="outlined"
            type="button"
            onClick={handleClose}
          >
            Close
          </Button>
        </div>
      </FormProvider>
    </Dialog>
  );
}
